'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import type { SyntheticEvent } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import type { ChipProps } from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import { DateRangePicker } from './DateRangePicker.js';

export interface FilterSelectOption {
  value: string;
  label: string;
  /** Chip color for the selected-value summary (multi-select only). */
  color?: ChipProps['color'];
}

interface FilterFieldBase {
  name: string;
  label: string;
}

export interface FilterSelectField extends FilterFieldBase {
  type: 'select';
  options: FilterSelectOption[];
  placeholder?: string;
  /** Omits the "All"/placeholder option — use for fields that always need a value. */
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
}

export interface FilterMultiSelectField extends FilterFieldBase {
  type: 'multi-select';
  options: FilterSelectOption[];
  placeholder?: string;
}

export interface FilterSearchField extends FilterFieldBase {
  type: 'search';
  placeholder?: string;
  debounceMs?: number;
}

export interface FilterAutocompleteField extends FilterFieldBase {
  type: 'autocomplete';
  options: FilterSelectOption[];
  multiple?: boolean;
  placeholder?: string;
}

export interface FilterTagsField extends FilterFieldBase {
  type: 'tags';
  options?: FilterSelectOption[];
  placeholder?: string;
}

export interface FilterDateRangeField extends FilterFieldBase {
  type: 'date-range';
  disabled?: boolean;
  fromLabel?: string;
  toLabel?: string;
}

export type FilterField =
  | FilterSelectField
  | FilterMultiSelectField
  | FilterSearchField
  | FilterAutocompleteField
  | FilterTagsField
  | FilterDateRangeField;

export interface DateRangeValue {
  from?: string;
  to?: string;
}

export interface FilterBarProps {
  value: Record<string, unknown>;
  onChange: (value: Record<string, unknown>) => void;
  fields: FilterField[];
  activeCount?: number;
  onClearAll?: () => void;
  filtersLabel?: string;
  clearLabel?: string;
}

function getFieldValue<T>(value: Record<string, unknown>, name: string): T | undefined {
  const v = value[name];
  return v as T | undefined;
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

function FilterSelect({
  field,
  value,
  onChange,
}: {
  field: FilterSelectField;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}) {
  return (
    <FormControl size="small" sx={{ minWidth: 160 }} disabled={field.disabled} error={field.error}>
      <InputLabel id={`${field.name}-label`}>{field.label}</InputLabel>
      <Select
        labelId={`${field.name}-label`}
        id={field.name}
        value={value ?? ''}
        label={field.label}
        onChange={(e) => onChange(e.target.value || undefined)}
        displayEmpty={false}
      >
        {!field.required && (
          <MenuItem value="">
            <em>{field.placeholder ?? 'All'}</em>
          </MenuItem>
        )}
        {field.options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function FilterMultiSelect({
  field,
  value,
  onChange,
}: {
  field: FilterMultiSelectField;
  value: string[] | undefined;
  onChange: (value: string[] | undefined) => void;
}) {
  const selected = value ?? [];
  return (
    <FormControl size="small" sx={{ minWidth: 180 }}>
      <InputLabel id={`${field.name}-label`}>{field.label}</InputLabel>
      <Select
        labelId={`${field.name}-label`}
        id={field.name}
        multiple
        value={selected}
        label={field.label}
        onChange={(e) => {
          const raw = e.target.value;
          const next = typeof raw === 'string' ? raw.split(',') : (raw as string[]);
          onChange(next.length > 0 ? next : undefined);
        }}
        input={<OutlinedInput label={field.label} />}
        renderValue={(selectedValues) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {(selectedValues as string[]).map((v) => {
              const option = field.options.find((o) => o.value === v);
              return (
                <Chip
                  key={v}
                  label={option?.label ?? v}
                  size="small"
                  color={option?.color}
                  sx={{ height: 22 }}
                />
              );
            })}
          </Box>
        )}
      >
        {field.options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox checked={selected.includes(option.value)} size="small" />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function useDebouncedValue(
  value: string,
  onDebouncedChange: (value: string) => void,
  debounceMs: number
) {
  const [local, setLocal] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastEmittedRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Ignore the echo of our own commit; it would clobber newer keystrokes.
    if (value === lastEmittedRef.current) return;
    lastEmittedRef.current = undefined;
    setLocal(value);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  }, [value]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const emit = (next: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = undefined;
      lastEmittedRef.current = next;
      onDebouncedChange(next);
    }, debounceMs);
  };

  const handleChange = (next: string) => {
    setLocal(next);
    emit(next);
  };

  return { local, handleChange };
}

function FilterSearch({
  field,
  value,
  onChange,
}: {
  field: FilterSearchField;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}) {
  const { local, handleChange } = useDebouncedValue(
    value ?? '',
    (next) => onChange(next || undefined),
    field.debounceMs ?? 300
  );

  return (
    <TextField
      id={field.name}
      label={field.label}
      size="small"
      value={local}
      placeholder={field.placeholder}
      onChange={(e) => handleChange(e.target.value)}
      sx={{ minWidth: 180 }}
    />
  );
}

function FilterAutocomplete({
  field,
  value,
  onChange,
}: {
  field: FilterAutocompleteField;
  value: string | string[] | undefined;
  onChange: (value: string | string[] | undefined) => void;
}) {
  const options = field.options;
  if (field.multiple) {
    const selected = isStringArray(value) ? value : [];
    const selectedOptions = options.filter((o) => selected.includes(o.value));
    return (
      <Autocomplete
        id={field.name}
        multiple
        size="small"
        options={options}
        value={selectedOptions}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(o, v) => o.value === v.value}
        onChange={(_: SyntheticEvent, next) => {
          onChange(next.length > 0 ? next.map((o) => o.value) : undefined);
        }}
        renderInput={(params) => (
          <TextField {...params} label={field.label} placeholder={field.placeholder} />
        )}
        sx={{ minWidth: 200 }}
      />
    );
  }

  const selectedOption = isString(value) ? options.find((o) => o.value === value) ?? null : null;
  return (
    <Autocomplete
      id={field.name}
      size="small"
      options={options}
      value={selectedOption}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      onChange={(_: SyntheticEvent, next) => onChange(next?.value)}
      renderInput={(params) => (
        <TextField {...params} label={field.label} placeholder={field.placeholder} />
      )}
      sx={{ minWidth: 200 }}
    />
  );
}

function FilterTags({
  field,
  value,
  onChange,
}: {
  field: FilterTagsField;
  value: string[] | undefined;
  onChange: (value: string[] | undefined) => void;
}) {
  const options = useMemo(
    () => (field.options ? field.options.map((o) => o.label) : []),
    [field.options]
  );
  const selected = isStringArray(value) ? value : [];

  return (
    <Autocomplete
      id={field.name}
      multiple
      freeSolo
      size="small"
      options={options}
      value={selected}
      onChange={(_: SyntheticEvent, next) => onChange(next.length > 0 ? next : undefined)}
      renderInput={(params) => (
        <TextField {...params} label={field.label} placeholder={field.placeholder} />
      )}
      sx={{ minWidth: 200 }}
    />
  );
}


function FilterFieldRenderer({
  field,
  value,
  onChange,
}: {
  field: FilterField;
  value: unknown;
  onChange: (name: string, value: unknown) => void;
}) {
  const handleChange = (next: unknown) => onChange(field.name, next);

  switch (field.type) {
    case 'select':
      return <FilterSelect field={field} value={getFieldValue<string>(value as Record<string, unknown>, field.name)} onChange={handleChange} />;
    case 'multi-select':
      return <FilterMultiSelect field={field} value={getFieldValue<string[]>(value as Record<string, unknown>, field.name)} onChange={handleChange} />;
    case 'search':
      return <FilterSearch field={field} value={getFieldValue<string>(value as Record<string, unknown>, field.name)} onChange={handleChange} />;
    case 'autocomplete':
      return <FilterAutocomplete field={field} value={getFieldValue<string | string[]>(value as Record<string, unknown>, field.name)} onChange={handleChange} />;
    case 'tags':
      return <FilterTags field={field} value={getFieldValue<string[]>(value as Record<string, unknown>, field.name)} onChange={handleChange} />;
    case 'date-range':
      return (
        <DateRangePicker
          label={field.label}
          value={getFieldValue<DateRangeValue>(value as Record<string, unknown>, field.name)}
          onChange={handleChange}
          disabled={field.disabled}
          fromLabel={field.fromLabel}
          toLabel={field.toLabel}
        />
      );
    default:
      return null;
  }
}

export function FilterBar({
  value,
  onChange,
  fields,
  activeCount,
  onClearAll,
  filtersLabel = 'Filters',
  clearLabel = 'Clear all',
}: FilterBarProps) {
  // Debounced fields (search) schedule their commit via a closure captured at
  // keystroke time. If two such fields are edited within the same debounce
  // window, both timers can fire back-to-back before React has re-rendered
  // FilterBar with the first commit's result — there's no guarantee a render
  // happens between two independently-scheduled timeouts. Reading AND writing
  // through a ref (rather than the closed-over `value` prop) means the second
  // commit merges against what the first one just wrote, regardless of
  // whether a render happened in between; the ref is also resynced from
  // `value` on every render so it never drifts from the controlled prop.
  const valueRef = useRef(value);
  valueRef.current = value;

  const handleFieldChange = (name: string, next: unknown) => {
    const merged = { ...valueRef.current, [name]: next };
    valueRef.current = merged;
    onChange(merged);
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
      {fields.map((field) => (
        <FilterFieldRenderer key={field.name} field={field} value={value} onChange={handleFieldChange} />
      ))}

      {activeCount !== undefined && activeCount > 0 && onClearAll && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Badge badgeContent={activeCount} color="primary">
            <Chip label={filtersLabel} size="small" icon={<FilterListIcon />} />
          </Badge>
          <Button size="small" variant="text" startIcon={<CloseIcon />} onClick={onClearAll}>
            {clearLabel}
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default FilterBar;
